/**
 * Pure functions for streak calculation logic.
 * These functions contain no database dependencies and can be easily unit tested.
 */

import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { subDays } from "date-fns";

export interface StreakCheckInput {
  /** Array of session date strings in YYYY-MM-DD format */
  sessionDates: string[];
  /** Today's date string in YYYY-MM-DD format */
  todayString: string;
  /** User's timezone */
  timezone: string;
}

export interface StreakCheckResult {
  /** Whether the streak should continue */
  streakContinues: boolean;
  /** Whether today's session has already been counted */
  alreadyCountedToday: boolean;
  /** Whether the streak should be reset to zero */
  shouldResetToZero: boolean;
  /** User's timezone */
  timezone: string;
  /** Today's date string */
  todayString: string;
}

/**
 * Calculate streak status based on session dates.
 * 
 * Logic matches reset_expired_streaks() SQL function:
 * - If session yesterday OR session today → don't reset (streak continues)
 * - If NO session yesterday AND NO session today → check rolling 7-day window
 *   - If < 5 days worked in last 7 → reset to 0
 *   - If >= 5 days worked in last 7 → keep streak (streak continues)
 * 
 * @param input - Input data containing session dates, today's date, and timezone
 * @returns Streak check result with flags for continuation, reset, and today's status
 */
export function calculateStreakStatus(input: StreakCheckInput): StreakCheckResult {
  const { sessionDates, todayString, timezone } = input;
  
  // Get unique session days
  const sessionDays = new Set(sessionDates);
  const hasSessionToday = sessionDays.has(todayString);
  
  // Parse today's date in the user's timezone
  // Parse the date string components individually to avoid ambiguous timezone parsing
  // This ensures we create midnight in the user's timezone, not UTC
  const [year, month, day] = todayString.split('-').map(Number);
  const todayMidnightInTz = new Date(year, month - 1, day, 0, 0, 0, 0);
  const todayInUtc = fromZonedTime(todayMidnightInTz, timezone);
  
  // Calculate yesterday's date string in the same timezone
  const yesterdayInUtc = subDays(todayInUtc, 1);
  const yesterdayString = formatInTimeZone(yesterdayInUtc, timezone, 'yyyy-MM-dd');
  const hasSessionYesterday = sessionDays.has(yesterdayString);
  
  let streakContinues = false;
  let shouldResetToZero = false;
  
  // If session yesterday OR session today, don't reset (streak continues)
  if (hasSessionYesterday || hasSessionToday) {
    streakContinues = true;
    shouldResetToZero = false;
  } else {
    // No session yesterday AND no session today - check rolling 7-day window
    // Build array of last 7 days (including today): [today - 6, today]
    const last7Days: string[] = [];
    for (let i = 6; i >= 0; i--) {
      const dayInUtc = subDays(todayInUtc, i);
      const dayString = formatInTimeZone(dayInUtc, timezone, 'yyyy-MM-dd');
      last7Days.push(dayString);
    }
    
    // Count how many days in the last 7 have sessions
    const daysWithSessions = last7Days.filter(day => sessionDays.has(day)).length;
    
    // Must have at least 5 days worked in the last 7 days
    if (daysWithSessions >= 5) {
      streakContinues = true;
      shouldResetToZero = false;
    } else {
      // Less than 5 days worked - should reset to 0
      streakContinues = false;
      shouldResetToZero = true;
    }
  }
  
  return {
    streakContinues,
    alreadyCountedToday: hasSessionToday,
    shouldResetToZero,
    timezone: input.timezone,
    todayString,
  };
}

/**
 * Get today's date string in a specific timezone.
 * 
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns Date string in YYYY-MM-DD format
 */
export function getTodayString(timezone: string): string {
  const now = new Date();
  // Use formatInTimeZone for reliable timezone conversion
  // This matches SQL's DATE(CURRENT_TIMESTAMP AT TIME ZONE timezone)
  return formatInTimeZone(now, timezone, 'yyyy-MM-dd');
}

/**
 * Get yesterday's date string in a specific timezone.
 * Properly handles timezone boundaries to ensure we get yesterday in the user's timezone,
 * not yesterday in UTC.
 * 
 * @param timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns Date string in YYYY-MM-DD format
 */
export function getYesterdayString(timezone: string): string {
  // Get today's date string in the user's timezone
  const todayString = getTodayString(timezone);
  // Parse the date string components individually to avoid ambiguous timezone parsing
  // This ensures we create midnight in the user's timezone, not UTC
  const [year, month, day] = todayString.split('-').map(Number);
  const todayMidnightInTz = new Date(year, month - 1, day, 0, 0, 0, 0);
  const todayInUtc = fromZonedTime(todayMidnightInTz, timezone);
  // Calculate yesterday's date in UTC
  const yesterdayInUtc = subDays(todayInUtc, 1);
  // Format back to the user's timezone
  return formatInTimeZone(yesterdayInUtc, timezone, 'yyyy-MM-dd');
}

